import { faPlain, money, shortJalali } from 'utilities/helper/format'
import { Payable, daysUntilDue } from 'services/easyPay/payables'

/**
 * One payable row on the v2 pay screen.
 *
 * Hierarchy note: the FUND NAME is the primary line, not the payment type.
 * In a list where every row says «حق عضویت», the type is noise and the fund is
 * the only thing telling one row from another — so the fund name is what you
 * scan and the type is the qualifier under it.
 *
 * The whole row is the tap target. Requiring a hit on a 16px checkbox to
 * select a payment is a poor target on a phone.
 *
 * `description` is still what gets sent to the server — PaymentItem.description
 * consumes it — it is just no longer what the user reads.
 */
export default function PayableRow({
    item,
    onToggle,
}: {
    item: Payable
    onToggle: (key: string) => void
}) {
    const isRecord = item.bucket === 'record'

    const due = item.jalali_due_date ? shortJalali(item.jalali_due_date) : ''
    const kind =
        item.installment_index && item.installments_total
            ? `قسط ${faPlain.format(item.installment_index)} از ${faPlain.format(
                  item.installments_total
              )}`
            : item.type === 'psp'
            ? item.periods_behind && item.periods_behind > 1
                ? `حق عضویت — ${faPlain.format(item.periods_behind)} دوره عقب`
                : 'حق عضویت'
            : item.type === 'msp'
            ? 'سهم این دوره'
            : item.description
    const subtitle = [kind, due ? `سررسید ${due}` : ''].filter(Boolean).join(' · ')

    return (
        <div
            role={isRecord ? undefined : 'button'}
            tabIndex={isRecord ? undefined : 0}
            onClick={() => !isRecord && onToggle(item.key)}
            onKeyDown={(e) => {
                if (isRecord) return
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onToggle(item.key)
                }
            }}
            className={`flex gap-2 items-start bg-white border rounded-card p-3 ${
                isRecord
                    ? 'opacity-60 border-divider'
                    : `cursor-pointer ${
                          item.isSelected
                              ? 'border-green-1'
                              : 'border-divider'
                      }`
            }`}
        >
            <input
                type="checkbox"
                // Deliberately no `id`: every checkbox on this screen used to
                // share id="check-api-checkbox", making them the same element
                // as far as the DOM and assistive tech are concerned.
                checked={item.isSelected}
                disabled={isRecord}
                // The row handles the click; the box is a visual state that
                // must not double-fire the toggle when tapped directly.
                readOnly
                tabIndex={-1}
                aria-hidden="true"
                className="mt-[3px] w-4 h-4 accent-green-1 shrink-0 pointer-events-none"
            />
            <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-heading truncate">
                    {item.box_name}
                </p>
                <p className="text-xs text-text-secondary truncate">
                    {subtitle}
                </p>
                <div className="flex items-center justify-between gap-2 mt-1">
                    <span className="text-sm font-bold text-heading shrink-0">
                        {money(item.amount)} تومان
                    </span>
                    <Status item={item} isRecord={isRecord} />
                </div>
            </div>
        </div>
    )
}

function Status({ item, isRecord }: { item: Payable; isRecord: boolean }) {
    if (isRecord) {
        return item.manager_phone ? (
            <a
                href={`tel:${item.manager_phone}`}
                onClick={(e) => e.stopPropagation()}
                className="text-xs text-green-1 font-bold"
            >
                ☎ تماس با مدیر
            </a>
        ) : (
            <span className="text-xs text-text-muted">
                پرداخت نزد مدیر صندوق
            </span>
        )
    }

    // Not yet due: say WHEN, in neutral colours. These rows used to carry no
    // badge at all, so an installment landing in three days looked exactly
    // like one landing in four weeks. Red is reserved for things that are
    // actually late.
    if (item.bucket === 'upcoming') {
        const left = daysUntilDue(item)
        if (left === undefined) return <span />
        return (
            <span className="text-[11px] font-bold bg-divider text-text-secondary rounded-full px-2 py-[1px]">
                {left <= 0 ? 'به‌زودی' : `${faPlain.format(left)} روز دیگر`}
            </span>
        )
    }

    // Read the bucket, not the raw field: `bucket` already went through
    // isOverdue(), which falls back to the `dins` tag on a backend that has not
    // shipped `is_overdue` yet.
    if (item.bucket !== 'overdue') return <span />

    // `days_overdue` MISSING is not the same as zero. Without the distinction a
    // 56-days-late installment reads «امروز سررسید است» against a backend that
    // does not send the field — the most misleading thing on the screen.
    const days = item.days_overdue
    if (days === undefined) {
        return (
            <span className="text-[11px] font-bold bg-danger-soft text-danger-on rounded-full px-2 py-[1px]">
                سررسید شده
            </span>
        )
    }
    if (days > 0) {
        return (
            <span className="text-[11px] font-bold bg-danger-soft text-danger-on rounded-full px-2 py-[1px]">
                {faPlain.format(days)} روز تاخیر
            </span>
        )
    }
    return (
        <span className="text-[11px] font-bold bg-warning-soft text-warning-on rounded-full px-2 py-[1px]">
            امروز سررسید است
        </span>
    )
}
